import { put, take, fork, call, select, all } from "redux-saga/effects";
import { Ace2ClaimAction, Ace2ClaimScope } from "./types";
import {
  enableScopeLoading,
  disableScopeLoading,
} from "../scope-loading/actions";
import * as Api from "../../service/api";
import { ScopeErrorAction } from "../scope-error/types";
import {
  cleanUpClaimInfo,
  createAce2Claim,
  fetchClaimInfo,
  setClaimInfo,
  setDraftModal,
  setInProcessModal,
} from "./actions";
import RouteNames from "../../routes/type";
import { navigateTo } from "../../helper";
import {
  MapApiDamage,
  mapApiClaimItem,
  mapCustomerInfo,
  nextStepRedirect,
} from "./helpers";
import toast from "react-hot-toast";
import { RootState } from "../root-reducer";
import { Ace2Plan } from "../../types/plan";
import { mapApiAce2Plan } from "../user/helpers";
import axios from "axios";

function* createClaimFlow() {
  while (true) {
    const { payload } = yield take(Ace2ClaimAction.CREATE_CLAIM);
    const { warranty_id, showDraftModal, showClaimInProcessModal } = payload;
    try {
      yield put(enableScopeLoading(Ace2ClaimScope));
      const plan: Ace2Plan = yield select((state) =>
        state.user.plans.find((item) => item.id === warranty_id)
      );
      let claimId = yield select((state) => state.ace2Claim.claimId);
      if (!claimId) {
        const data = yield call(Api.fileAce2Claim.insertClaim, {
          warranty_id,
          contact_id: plan.customerId,
          internally_generated: plan.internallyGenerated,
        });
        if (data?.message === "CLAIM_DRAFT_FOUND") {
          showDraftModal?.(data?.claimId);
        } else if (data?.message === "CLAIM_IN_PROGRESS") {
          showClaimInProcessModal?.(data?.claimSatusCode);
        } else {
          claimId = data?.id;
          yield put(
            setClaimInfo({
              claimId,
              plan,
              status: "Draft",
              warranty_items: plan?.warrantyItems || [],
              customerId: plan?.customerId || "",
            })
          );
        }
      }
      if (claimId) {
        yield navigateTo(
          `/${RouteNames.CREATE_ACE2_CLAIM}?step=2&claimId=${claimId}&warranty_id=${warranty_id}`
        );
      } else {
        yield navigateTo(`/${RouteNames.SELECT_PLAN}`, { replace: true });
      }
    } catch (error) {
      yield put({
        type: ScopeErrorAction.SET_SCOPE_ERROR,
        payload: Ace2ClaimScope,
        error,
      });
    } finally {
      yield put(disableScopeLoading(Ace2ClaimScope));
    }
  }
}

function* deleteDraftAndCreateClaimFlow() {
  while (true) {
    const request = yield take(Ace2ClaimAction.DELETE_CLAIM_CREATE_NEW);
    yield put(enableScopeLoading(Ace2ClaimScope));
    const { warranty_id, claimId } = request.payload;
    try {
      yield put(cleanUpClaimInfo());
      yield call(Api.fileAce2Claim.deleteClaim, claimId);
      yield put(createAce2Claim({ warranty_id, showDraftModal: () => {} }));
    } catch (error) {
      console.log("error:", error);
      yield put({
        type: ScopeErrorAction.SET_SCOPE_ERROR,
        payload: Ace2ClaimScope,
        error,
      });
      yield put(disableScopeLoading(Ace2ClaimScope));
    }
  }
}

function* insertClaimItemsFlow() {
  while (true) {
    const request = yield take(Ace2ClaimAction.INSERT_CLAIM_ITEMS);
    yield put(enableScopeLoading(Ace2ClaimScope));
    try {
      const claimId = yield select((state) => state.ace2Claim.claimId);
      const warranty_items = yield select(
        (state) => state.ace2Claim.warranty_items
      );
      const claim_items = yield select(
        (state: RootState) => state?.ace2Claim?.claim_items || []
      );
      const requestedData = request.payload;

      // const dataNeedToProceed = requestedData.filter((item) => {
      //   const claim_item = claim_items?.find(({ id }) => id === item?.id) || {};
      //   if (
      //     isEqual(
      //       {
      //         manufacturer: claim_item.manufacturer,
      //         material: claim_item.material,
      //         color: claim_item.color,
      //       },
      //       {
      //         manufacturer: item.manufacturer,
      //         material: item.material,
      //         color: item.color,
      //       }
      //     )
      //   ) {
      //     return false;
      //   }
      //   return true;
      // });

      const data = yield call(
        Api.fileAce2Claim.insertClaimItems,
        claimId,
        requestedData
      );
      const mappeddata = data.map((item, idx) => ({
        damages: [],
        documents_and_photos: [],
        ...(claim_items?.find(({ id }) => id === item?.details?.id) || {}),
        id: item?.details?.id,
        manufacturer: requestedData[idx].manufacturer,
        material: requestedData[idx].material,
        color: requestedData[idx].color,
        warrantyItem: warranty_items.find(
          ({ warranty_line_item_guid }) =>
            warranty_line_item_guid ===
            requestedData[idx].warranty_line_item_guid
        ),
      }));
      yield put(setClaimInfo({ claim_items: mappeddata }));

      nextStepRedirect(4);
    } catch (error) {
      console.log("error:", error);
      yield put({
        type: ScopeErrorAction.SET_SCOPE_ERROR,
        payload: Ace2ClaimScope,
        error,
      });
    } finally {
      yield put(disableScopeLoading(Ace2ClaimScope));
    }
  }
}

function* removeClaimItemsFlow() {
  while (true) {
    const request = yield take(Ace2ClaimAction.REMOVE_CLAIM_ITEM);
    yield put(enableScopeLoading(Ace2ClaimScope));
    try {
      const { id: claimItemId } = request.payload;
      yield call(Api.fileAce2Claim.removeClaimItem, claimItemId);
      const data = yield select(
        (state: RootState) => state.ace2Claim.claim_items
      );
      const mappeddata = data.filter((item) => item.id !== claimItemId);
      yield put(setClaimInfo({ claim_items: mappeddata }));
    } catch (error) {
      yield put({
        type: ScopeErrorAction.SET_SCOPE_ERROR,
        payload: Ace2ClaimScope,
        error,
      });
    } finally {
      yield put(disableScopeLoading(Ace2ClaimScope));
    }
  }
}

function* insertClaimItemDamagesFlow() {
  while (true) {
    const request = yield take(Ace2ClaimAction.INSERT_CLAIM_ITEM_DAMGES);
    yield put(enableScopeLoading(Ace2ClaimScope));
    try {
      const claimItemDamageData = request?.payload || [];
      const data = yield call(
        Api.fileAce2Claim.insertClaimItemDamages,
        claimItemDamageData
      );

      const mappedDamagedata = claimItemDamageData.map((item, idx) => ({
        ...MapApiDamage(item),
        claim_item_id: item?.claim_item_id,
        id: data[idx]?.details?.id,
      }));

      const claim_items = yield select(
        (state: RootState) => state.ace2Claim.claim_items
      );
      const claim_items_damages = claim_items.map((item) => ({
        ...item,
        damages: mappedDamagedata.filter(
          ({ claim_item_id }) => claim_item_id === item.id
        ),
      }));

      yield put(setClaimInfo({ claim_items: claim_items_damages }));
      nextStepRedirect(5);
    } catch (error) {
      yield put({
        type: ScopeErrorAction.SET_SCOPE_ERROR,
        payload: Ace2ClaimScope,
        error,
      });
    } finally {
      yield put(disableScopeLoading(Ace2ClaimScope));
    }
  }
}

function* submitClaimFlow() {
  while (true) {
    yield take(Ace2ClaimAction.SUBMIT_CLAIM);
    yield put(enableScopeLoading(Ace2ClaimScope));
    try {
      const claimId = yield select((state) => state.ace2Claim.claimId);
      const oldStatus = yield select(
        (state: RootState) => state.ace2Claim.status
      );
      const data = yield call(
        Api.fileAce2Claim.submitClaim,
        claimId,
        oldStatus
      );
      if (data?.code === "SUCCESS") {
        const warranty_id = yield select((state) => state.ace2Claim.plan.id);
        yield put(
          fetchClaimInfo({
            claimId,
            warranty_id,
          })
        );
        nextStepRedirect(8);
      } else {
        yield put(disableScopeLoading(Ace2ClaimScope));
      }
    } catch (error) {
      yield put({
        type: ScopeErrorAction.SET_SCOPE_ERROR,
        payload: Ace2ClaimScope,
        error,
      });
      yield put(disableScopeLoading(Ace2ClaimScope));
    }
  }
}

function* updateCustomerInfoFlow() {
  while (true) {
    const { payload } = yield take(Ace2ClaimAction.UPDATE_CUSTOMER_INFO);
    yield put(enableScopeLoading(Ace2ClaimScope));
    try {
      const customerId = yield select((state) => state.ace2Claim.customerId);
      const customer_data = yield select(
        (state) => state.ace2Claim.customer_data
      );
      yield call(Api.fileAce2Claim.updateCustomerInfo, customerId, payload);
      yield put(
        setClaimInfo({
          customer_data: { ...customer_data, ...mapCustomerInfo(payload) },
        })
      );
      nextStepRedirect(6);
    } catch (error) {
      yield put({
        type: ScopeErrorAction.SET_SCOPE_ERROR,
        payload: Ace2ClaimScope,
        error,
      });
    } finally {
      yield put(disableScopeLoading(Ace2ClaimScope));
    }
  }
}

function* deleteClaimDamageItemFlow() {
  while (true) {
    const request = yield take(Ace2ClaimAction.DELETE_DAMAGE_ITEM);
    yield put(enableScopeLoading(Ace2ClaimScope));
    try {
      yield call(Api.fileAce2Claim.deleteClaimDamageItem, request.payload);
    } catch (error) {
      yield put({
        type: ScopeErrorAction.SET_SCOPE_ERROR,
        payload: Ace2ClaimScope,
        error,
      });
    } finally {
      yield put(disableScopeLoading(Ace2ClaimScope));
    }
  }
}

function* fetchClaimInfoFlow() {
  while (true) {
    const request = yield take(Ace2ClaimAction.FETCH_CLAIM_INFO);
    yield put(enableScopeLoading(Ace2ClaimScope));
    try {
      const { claimId, warranty_id } = request.payload;
      let plan = {};
      let claim = {};
      let warranty_items = [];
      let mapped_claim_items = [];
      let cusomerInfo = {};
      if (warranty_id) {
        plan = yield call(Api.fileAce2Claim.getWarrnty, warranty_id);
        plan = mapApiAce2Plan(plan);
        warranty_items = plan?.["warrantyItems"] || [];
      }
      if (claimId) {
        const tempclaim = yield call(Api.fileAce2Claim.getClaim, claimId);
        const {
          claim_items = [],
          claim_items_damages = [],
          Contact = {},
        } = tempclaim || {};
        cusomerInfo = Contact;
        claim = tempclaim;
        mapped_claim_items = claim_items.map((item) => ({
          ...mapApiClaimItem(item),
          warrantyItem:
            warranty_items?.find(
              ({ warranty_line_item_guid }) =>
                warranty_line_item_guid === item.warranty_line_item_guid
            ) || {},
          damages: claim_items_damages
            .filter((damage) => damage?.claim_item_id?.id === item.id)
            .map(MapApiDamage),
        }));
      }
      yield put(
        setClaimInfo({
          plan,
          claimId,
          warranty_items,
          claim_items: mapped_claim_items,
          data: claim,
          status: claim["bp_status"] || "",
          customerId: cusomerInfo?.["id"],
          customer_data: mapCustomerInfo(cusomerInfo),
        })
      );
    } catch (error) {
      yield put({
        type: ScopeErrorAction.SET_SCOPE_ERROR,
        payload: Ace2ClaimScope,
        error: "CLAIM_NOT_FOUND",
      });
    } finally {
      yield put(disableScopeLoading(Ace2ClaimScope));
    }
  }
}

function* claimItemUploadFlow() {
  while (true) {
    const request = yield take(Ace2ClaimAction.CLAIM_ITEM_UPLOAD);
    yield put(enableScopeLoading(Ace2ClaimScope));
    try {
      const { files, claimItemId, callBack } = request.payload;
      const claimId = yield select((state) => state.ace2Claim.claimId);
      const claim_items = yield select(
        (state: RootState) => state.ace2Claim.claim_items
      );

      const fileNames = yield all(
        files.map(function* (item) {
          const { url } = yield call(
            Api.fileAce2Claim.getPreSignedUrl,
            claimId,
            item.type
          );
          yield axios.put(url, item, {
            headers: {
              "Content-Type": item.type,
            },
          });
          const data = new URL(url);
          data.search = "";
          return data.toString().split("/").pop();
        })
      );

      if (fileNames.length > 0) {
        const claim_item_data = claim_items.find(
          ({ id }) => id === claimItemId
        );

        const oldNewCombined = [
          ...(claim_item_data?.documents_and_photos || []),
          ...(fileNames || []),
        ];

        yield call(Api.fileAce2Claim.claimItemSaveFileInfo, claimItemId, {
          fileIds: oldNewCombined.join(","),
        });

        yield put(
          setClaimInfo({
            claim_items: claim_items.map((item) =>
              item.id === claim_item_data.id
                ? {
                    ...item,
                    documents_and_photos: oldNewCombined,
                  }
                : item
            ),
          })
        );
        toast.success("files uploaded sucessfully", {
          position: "bottom-center",
        });
        callBack && callBack();
      }
    } catch (error) {
      toast.error("file not uploaded.", {
        position: "bottom-center",
      });
      yield put({
        type: ScopeErrorAction.SET_SCOPE_ERROR,
        payload: Ace2ClaimScope,
        error,
      });
    } finally {
      yield put(disableScopeLoading(Ace2ClaimScope));
    }
  }
}

function* claimItemDeleteImageFlow() {
  while (true) {
    const request = yield take(Ace2ClaimAction.CLAIM_ITEM_DELETE_IMAGE);
    yield put(enableScopeLoading(Ace2ClaimScope));
    try {
      const { fileName, claimItemId } = request.payload;
      const claimId = yield select((state) => state.ace2Claim.claimId);
      const claim_items = yield select(
        (state: RootState) => state.ace2Claim.claim_items
      );
      const removeFile = yield call(Api.fileAce2Claim.removeClaimPhoto, {
        claimId,
        fileName,
      });

      const claim_item_data = claim_items.find(({ id }) => id === claimItemId);
      if (removeFile) {
        const removedFileData = claim_item_data?.documents_and_photos?.filter(
          (item) => item !== fileName
        );

        yield call(Api.fileAce2Claim.claimItemSaveFileInfo, claimItemId, {
          fileIds: removedFileData.join(","),
        });

        yield put(
          setClaimInfo({
            claim_items: claim_items.map((item) =>
              item.id === claim_item_data.id
                ? {
                    ...item,
                    documents_and_photos: removedFileData,
                  }
                : item
            ),
          })
        );
        toast.success("file deleted sucessfully", {
          position: "bottom-center",
        });
      } else {
        toast.error("something went wrong, can't delete file.", {
          position: "bottom-center",
        });
      }
      if (fileName) {
      }
    } catch (error) {
      toast.error("file not uploaded.", {
        position: "bottom-center",
      });
      yield put({
        type: ScopeErrorAction.SET_SCOPE_ERROR,
        payload: Ace2ClaimScope,
        error,
      });
    } finally {
      yield put(disableScopeLoading(Ace2ClaimScope));
    }
  }
}

export default function* userSaga() {
  yield fork(createClaimFlow);
  yield fork(fetchClaimInfoFlow);
  yield fork(deleteDraftAndCreateClaimFlow);
  yield fork(insertClaimItemDamagesFlow);
  yield fork(removeClaimItemsFlow);
  yield fork(insertClaimItemsFlow);
  yield fork(submitClaimFlow);
  yield fork(claimItemUploadFlow);
  yield fork(claimItemDeleteImageFlow);
  yield fork(deleteClaimDamageItemFlow);
  yield fork(updateCustomerInfoFlow);
}
